import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import authService from '../services/authService';
import { useAnalytics } from '../hooks/useAnalytics';
import socketClient from '../services/socketClient';
import featureFlags from '../config/featureFlags';
import Toggle from './shared/Toggle/Toggle';
import './Forms.css';

const RoomCodeEntry = () => {
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');

  // Check for RHP testing parameter in URL (?rhp=true)
  const urlParams = new URLSearchParams(window.location.search);
  const enableRHPForTesting = urlParams.get('rhp') === 'true';
  const showRHPRules = featureFlags.ENABLE_RHP_RULES || enableRHPForTesting;

  // Analytics tracking
  const { trackGameCreated, trackError, trackFeature, trackClick, trackEngagement } =
    useAnalytics();

  // Admin settings state
  const [roomSettings, setRoomSettings] = useState({
    maxPlayers: 6,
    roundsToWin: process.env.NODE_ENV === 'production' ? 10 : 3,
    isPrivate: true,
    allowSpectators: true,
    // TIMER SYSTEM: Smart Last Player + Escalation settings
    timersEnabled: true,
    lastPlayerTimer: 60, // seconds for last player
    escalationTimer: 240, // seconds total before escalation (4 minutes)
    escalationThreshold: 2, // trigger when 2+ players idle
    autoSubmitBehavior: 'random', // 'random', 'first', 'skip'
    // WRITE-IN CARDS SYSTEM
    writeInsEnabled: true, // Default enabled, room creator can disable
    // POOP RULE: First Judge designation
    poopRuleEnabled: window.location.hostname === 'localhost', // Default enabled on localhost for testing
    // RENO HORRIBLE PEOPLE HOUSE RULES
    rhpRulesEnabled: window.location.hostname === 'localhost', // Default enabled on localhost for testing
    rhpUBIEnabled: window.location.hostname === 'localhost', // Universal Basic Income: everyone starts with 1 point
    rhpWhitePrivilegeEnabled: window.location.hostname === 'localhost', // Default enabled on localhost for testing
    rhpDiscardEnabled: window.location.hostname === 'localhost', // Default enabled on localhost for testing
    rhpDoubleAwardEnabled: window.location.hostname === 'localhost', // Default enabled on localhost for testing
    rhpEnhancedWageringEnabled: window.location.hostname === 'localhost', // Wager multiple chips - default enabled on localhost for testing
  });

  const [showCustomize, setShowCustomize] = useState(false);

  const navigate = useNavigate();
  const { requireAuth, username } = useAuth();

  useEffect(() => {
    // Check if user has a token using the hook
    requireAuth();
  }, [requireAuth]);

  // Track when users view the room creation page for more than 5 seconds
  useEffect(() => {
    const engagementTimer = setTimeout(() => {
      trackEngagement('room_creation_engaged', {
        duration_seconds: 5,
        settings_viewed: true,
      });
    }, 5000);

    return () => clearTimeout(engagementTimer);
  }, [trackEngagement]);

  const handleSettingChange = (setting, value) => {
    // Special handling: when master RHP toggle is turned OFF, disable all individual rules
    if (setting === 'rhpRulesEnabled' && value === false) {
      setRoomSettings((prev) => ({
        ...prev,
        rhpRulesEnabled: false,
        rhpUBIEnabled: false,
        rhpWhitePrivilegeEnabled: false,
        rhpDiscardEnabled: false,
        rhpDoubleAwardEnabled: false,
        rhpEnhancedWageringEnabled: false,
      }));
    } else {
      setRoomSettings((prev) => ({
        ...prev,
        [setting]: value,
      }));
    }

    // Track non-default settings changes
    const defaults = {
      maxPlayers: 6,
      roundsToWin: process.env.NODE_ENV === 'production' ? 10 : 3,
      isPrivate: true,
      allowSpectators: true,
      timersEnabled: true,
      lastPlayerTimer: 60,
      escalationTimer: 240,
      escalationThreshold: 2,
      autoSubmitBehavior: 'random',
      writeInsEnabled: true,
      poopRuleEnabled: window.location.hostname === 'localhost',
      rhpRulesEnabled: window.location.hostname === 'localhost',
      rhpUBIEnabled: window.location.hostname === 'localhost',
      rhpWhitePrivilegeEnabled: window.location.hostname === 'localhost',
      rhpDiscardEnabled: window.location.hostname === 'localhost',
      rhpDoubleAwardEnabled: window.location.hostname === 'localhost',
      rhpEnhancedWageringEnabled: window.location.hostname === 'localhost',
    };

    if (value !== defaults[setting]) {
      trackFeature('room_setting_changed', {
        setting: setting,
        value: value,
        is_custom: true,
      });
    }
  };

  const handleCreateRoom = async () => {
    setIsCreating(true);
    setError('');

    // Track room creation attempt
    trackClick('create_room_submit', 'primary_action');

    try {
      // Connect to WebSocket server first
      socketClient.connect();

      // Wait for connection with increased timeout for Render startup
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Connection timeout - please try again'));
        }, 50000); // Increased to 50s to handle Render free tier cold starts (30-90s)

        if (socketClient.isSocketConnected()) {
          clearTimeout(timeout);
          resolve();
          return;
        }

        socketClient.on('connect', () => {
          clearTimeout(timeout);
          resolve();
        });

        socketClient.on('connect_error', (error) => {
          clearTimeout(timeout);
          reject(error);
        });
      });

      // Request room code generation from backend
      const gameSettings = {
        maxRounds: roomSettings.roundsToWin,
        maxPlayers: roomSettings.maxPlayers,
        // TIMER SYSTEM: Pass complete timer configuration
        timersEnabled: roomSettings.timersEnabled,
        lastPlayerTimer: roomSettings.lastPlayerTimer,
        escalationTimer: roomSettings.escalationTimer,
        escalationThreshold: roomSettings.escalationThreshold,
        autoSubmitBehavior: roomSettings.autoSubmitBehavior,
        // WRITE-IN CARDS SYSTEM
        writeInsEnabled: roomSettings.writeInsEnabled,
        // POOP RULE SYSTEM
        poopRuleEnabled: roomSettings.poopRuleEnabled,
        // RENO HORRIBLE PEOPLE HOUSE RULES
        rhpUBIEnabled: roomSettings.rhpUBIEnabled,
        rhpWhitePrivilegeEnabled: roomSettings.rhpWhitePrivilegeEnabled,
        rhpDiscardEnabled: roomSettings.rhpDiscardEnabled,
        rhpDoubleAwardEnabled: roomSettings.rhpDoubleAwardEnabled,
        rhpEnhancedWageringEnabled: roomSettings.rhpEnhancedWageringEnabled,
      };

      // Join room - backend will auto-generate the room code
      const newRoomCode = await socketClient.joinRoom(
        null,
        username,
        authService.getToken(),
        gameSettings
      );

      // Track successful room creation with all settings
      trackGameCreated(newRoomCode, {
        maxPlayers: roomSettings.maxPlayers,
        roundsToWin: roomSettings.roundsToWin,
        isPrivate: roomSettings.isPrivate,
        // TIMER SYSTEM: Track timer settings
        timersEnabled: roomSettings.timersEnabled,
        lastPlayerTimer: roomSettings.lastPlayerTimer,
        escalationTimer: roomSettings.escalationTimer,
        escalationThreshold: roomSettings.escalationThreshold,
        autoSubmitBehavior: roomSettings.autoSubmitBehavior,
        allowSpectators: roomSettings.allowSpectators,
        // WRITE-IN CARDS SYSTEM: Track write-ins setting
        writeInsEnabled: roomSettings.writeInsEnabled,
        // POOP RULE SYSTEM: Track poop rule setting
        poopRuleEnabled: roomSettings.poopRuleEnabled,
        estimatedDuration: getEstimatedDuration(),
        roundsDescription: getRoundsDescription(roomSettings.roundsToWin),
      });

      // Store room data and settings
      localStorage.setItem('currentRoom', newRoomCode);
      localStorage.setItem('isHost', 'true');
      localStorage.setItem('isSpectator', 'false');
      localStorage.setItem('gameSettings', JSON.stringify(gameSettings));

      console.log('Created room with settings:', newRoomCode, roomSettings);

      // Navigate to lobby
      navigate('/game-lobby');
    } catch (err) {
      console.error('Failed to create room:', err);

      // Track room creation failure
      trackError('room_creation_failed', {
        error: err.message,
        errorType: err.name,
        settings: roomSettings,
      });

      setError('Failed to create room. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const getEstimatedDuration = () => {
    const avgRoundTime = roomSettings.timerLength + 30; // Timer + discussion time
    const totalRounds = roomSettings.roundsToWin;
    const estimatedMinutes = Math.ceil((avgRoundTime * totalRounds) / 60);

    if (estimatedMinutes < 10) return `~${estimatedMinutes} minutes`;
    if (estimatedMinutes < 60) return `~${estimatedMinutes} minutes`;
    return `~${Math.ceil(estimatedMinutes / 60)} hour${estimatedMinutes > 120 ? 's' : ''}`;
  };

  // Helper function to get rounds description
  const getRoundsDescription = (rounds) => {
    if (rounds <= 3) return 'Quick';
    if (rounds <= 7) return 'Standard';
    if (rounds <= 15) return 'Long';
    if (rounds <= 25) return 'Marathon';
    return 'Epic';
  };

  // Track customize settings toggle
  const handleCustomizeToggle = () => {
    setShowCustomize(!showCustomize);
    trackFeature('customize_settings_toggled', {
      expanded: !showCustomize,
    });
  };

  // Track change username click
  const handleChangeUsername = () => {
    trackClick('change_username', 'navigation');

    // Reset usernameConfirmed flag so user can change their name
    const currentUser = authService.getUser();
    if (currentUser) {
      currentUser.usernameConfirmed = false;
      localStorage.setItem('user', JSON.stringify(currentUser));
    }

    // Redirect to username selection page
    navigate('/choose-username');
  };

  // Track back to home click
  const handleBackToHome = () => {
    trackClick('back_to_home', 'navigation');
    navigate('/');
  };

  return (
    <div className="room-entry-container">
      <div className="room-entry-card">
        <div className="user-info">
          <span className="username-display">Playing as: {username}</span>
          <button className="change-username-btn" onClick={handleChangeUsername}>
            Change
          </button>
        </div>

        <h1 className="room-entry-title">Create Game</h1>
        <p className="room-entry-subtitle">Configure your game settings and invite friends</p>

        {/* Game Settings Section */}
        <div className="game-settings-section">
          <h2 className="settings-title">Game Settings</h2>

          {/* Primary Setting: Game Length */}
          <div className="setting-card primary">
            <div className="setting-header">
              <span className="setting-icon">🎯</span>
              <h3 className="setting-title">Game Length</h3>
            </div>
            <p className="setting-description">
              Choose how many rounds to play. Quick games are 3-7 rounds, while epic games can go up
              to 50 rounds!
            </p>
            <div className="rounds-slider-container">
              <input
                type="range"
                min="3"
                max="50"
                value={roomSettings.roundsToWin}
                onChange={(e) => handleSettingChange('roundsToWin', parseInt(e.target.value))}
                className="rounds-slider"
                aria-label="Game length in rounds"
              />
              <div className="slider-display">
                <span className="slider-value">{roomSettings.roundsToWin}</span>
                <span className="slider-description">
                  rounds ({getRoundsDescription(roomSettings.roundsToWin)})
                </span>
              </div>
              <div className="slider-labels">
                <span>3</span>
                <span>50</span>
              </div>
            </div>
            <div className="setting-group">
              <label className="setting-label" htmlFor="maxPlayersSlider">
                Max Players
              </label>
              <div className="rounds-slider-container">
                <input
                  id="maxPlayersSlider"
                  type="range"
                  min="3"
                  max="10"
                  value={roomSettings.maxPlayers}
                  onChange={(e) => handleSettingChange('maxPlayers', parseInt(e.target.value))}
                  className="rounds-slider"
                />
                <div className="slider-display">
                  <span className="slider-value">{roomSettings.maxPlayers}</span>
                  <span className="slider-description">players</span>
                </div>
                <div className="slider-labels">
                  <span>3</span>
                  <span>10</span>
                </div>
              </div>
            </div>
          </div>

          {/* Smart Defaults Notice */}
          <div className="defaults-notice">
            <h3 className="defaults-title">🎯 Smart Defaults Enabled</h3>
            <ul className="defaults-list">
              <li>Smart timer system keeps games moving</li>
              <li>Write-in cards enabled for creativity</li>
              <li>Private room for friends only</li>
              <li>Perfect for 4-6 players</li>
            </ul>
          </div>

          {/* Customize Button */}
          <button className="customize-btn" onClick={handleCustomizeToggle} type="button">
            <span>⚙️</span>
            <span>{showCustomize ? 'Hide Custom Rules' : 'Customise Game Rules'}</span>
            <span>{showCustomize ? '▲' : '▼'}</span>
          </button>

          {/* Advanced Settings Wrapper */}
          <div className={`advanced-settings-wrapper ${showCustomize ? 'expanded' : ''}`}>
            <div className="advanced-settings">
              {/* TIMER SYSTEM Card */}
              <div className="setting-card">
                <div className="setting-header">
                  <span className="setting-icon">⏰</span>
                  <span className="setting-title">Smart Timers</span>
                </div>
                <p className="setting-description">
                  Hybrid system that only pressures the last player or multiple idle players after a
                  set time period
                </p>
                <div className="setting-control">
                  <div className="control-row">
                    <div className="setting-group">
                      <Toggle
                        checked={roomSettings.timersEnabled}
                        onChange={(checked) => handleSettingChange('timersEnabled', checked)}
                        label="Enable Timers"
                        enabledText="Timers Enabled"
                        disabledText="Timers Disabled"
                        id="timersEnabled"
                      />
                    </div>

                    <div className="setting-group">
                      <label className="setting-label" htmlFor="lastPlayerTimerSelect">
                        Last Player Timer
                      </label>
                      <select
                        id="lastPlayerTimerSelect"
                        className="setting-select"
                        value={roomSettings.lastPlayerTimer}
                        onChange={(e) =>
                          handleSettingChange('lastPlayerTimer', parseInt(e.target.value))
                        }
                        disabled={!roomSettings.timersEnabled}
                        title="How long the last remaining player has to submit"
                      >
                        <option value={30}>30 seconds</option>
                        <option value={45}>45 seconds</option>
                        <option value={60}>60 seconds (Recommended)</option>
                        <option value={90}>90 seconds</option>
                        <option value={120}>2 minutes</option>
                      </select>
                    </div>
                  </div>

                  <div className="control-row">
                    <div className="setting-group">
                      <label className="setting-label" htmlFor="escalationTimerSelect">
                        Escalation Time
                      </label>
                      <select
                        id="escalationTimerSelect"
                        className="setting-select"
                        value={roomSettings.escalationTimer}
                        onChange={(e) =>
                          handleSettingChange('escalationTimer', parseInt(e.target.value))
                        }
                        disabled={!roomSettings.timersEnabled}
                        title="Total time before escalation timer kicks in for multiple idle players"
                      >
                        <option value={180}>3 minutes</option>
                        <option value={240}>4 minutes (Recommended)</option>
                        <option value={300}>5 minutes</option>
                        <option value={360}>6 minutes</option>
                      </select>
                    </div>

                    <div className="setting-group">
                      <label className="setting-label" htmlFor="autoSubmitBehaviorSelect">
                        Card Auto-Submit
                        <span
                          className="info-icon"
                          title="Random: Submits a random card from your hand (fair). First: Always submits the first card (predictable)."
                        >
                          ℹ️
                        </span>
                      </label>
                      <select
                        id="autoSubmitBehaviorSelect"
                        className="setting-select"
                        value={roomSettings.autoSubmitBehavior}
                        onChange={(e) => handleSettingChange('autoSubmitBehavior', e.target.value)}
                        disabled={!roomSettings.timersEnabled}
                      >
                        <option value="random">Random</option>
                        <option value="first">First</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* WRITE-IN CARDS Card */}
              <div className="setting-card">
                <div className="setting-header">
                  <span className="setting-icon">✏️</span>
                  <span className="setting-title">Write-In Cards</span>
                </div>
                <p className="setting-description">
                  Players can write custom answer cards during gameplay (max 200 characters)
                </p>
                <div className="setting-control">
                  <div className="setting-group">
                    <Toggle
                      checked={roomSettings.writeInsEnabled}
                      onChange={(checked) => handleSettingChange('writeInsEnabled', checked)}
                      label="Allow Write-In Cards"
                      enabledText="Write-Ins Enabled"
                      disabledText="Write-Ins Disabled"
                      id="writeInsEnabled"
                    />
                  </div>
                </div>
              </div>

              {/* POOP RULE Card */}
              <div className="setting-card">
                <div className="setting-header">
                  <span className="setting-icon">💩</span>
                  <span className="setting-title">Poop Rule</span>
                </div>
                <p className="setting-description">
                  Room owner designates who pooped most recently to be first Judge
                </p>
                <div className="setting-control">
                  <div className="setting-group">
                    <Toggle
                      checked={roomSettings.poopRuleEnabled}
                      onChange={(checked) => handleSettingChange('poopRuleEnabled', checked)}
                      label="Enable Poop Rule"
                      enabledText="Poop Rule Enabled"
                      disabledText="Poop Rule Disabled"
                      id="poopRuleEnabled"
                    />
                  </div>
                </div>
              </div>

              {/* Room Settings Card */}
              <div className="setting-card">
                <div className="setting-header">
                  <span className="setting-icon">🔒</span>
                  <h3 className="setting-title">Room Settings</h3>
                </div>
                <p className="setting-description">
                  All rooms are invite-only for privacy. Configure voyeur settings below.
                </p>
                <div className="setting-control">
                  <div className="setting-group-full">
                    <label className="setting-checkbox">
                      <input
                        type="checkbox"
                        checked={roomSettings.allowSpectators}
                        onChange={(e) => handleSettingChange('allowSpectators', e.target.checked)}
                      />
                      <span className="checkmark"></span>
                      Allow voyeurs to watch (non-playing viewers)
                    </label>
                  </div>
                </div>
              </div>

              {/* RENO HORRIBLE PEOPLE HOUSE RULES Card */}
              {showRHPRules && (
                <div className="setting-card rhp-rules">
                  <div className="setting-header">
                    <span className="setting-icon">🎲</span>
                    <span className="setting-title">Reno Horrible People Rules</span>
                  </div>
                  <p className="setting-description rhp-warning">
                    ⚠️ <strong>Advanced Rules</strong> - These add strategic depth and chaos.
                    Recommended for experienced players only!
                  </p>
                  {/* MASTER TOGGLE */}
                  <div className="setting-control">
                    <div className="setting-group master-toggle">
                      <Toggle
                        checked={roomSettings.rhpRulesEnabled}
                        onChange={(checked) => handleSettingChange('rhpRulesEnabled', checked)}
                        label="Enable RHP Rules"
                        enabledText="RHP Rules Enabled"
                        disabledText="RHP Rules Disabled"
                        id="rhpRulesEnabled"
                      />
                      <p className="toggle-hint">Turn on to access individual rule options below</p>
                    </div>
                  </div>

                  {/* INDIVIDUAL RULES - Only visible when master toggle is ON */}
                  {roomSettings.rhpRulesEnabled && (
                    <div className="setting-control rhp-toggles">
                      <div className="setting-group">
                        <Toggle
                          checked={roomSettings.rhpUBIEnabled}
                          onChange={(checked) => handleSettingChange('rhpUBIEnabled', checked)}
                          label="Universal Basic Income"
                          enabledText="UBI Enabled"
                          disabledText="UBI Disabled"
                          id="rhpUBIEnabled"
                        />
                        <p className="toggle-hint">Everyone starts with 1 Buffalo Chip</p>
                      </div>
                      <div className="setting-group">
                        <Toggle
                          checked={roomSettings.rhpWhitePrivilegeEnabled}
                          onChange={(checked) =>
                            handleSettingChange('rhpWhitePrivilegeEnabled', checked)
                          }
                          label="White Privilege"
                          enabledText="White Privilege Enabled"
                          disabledText="White Privilege Disabled"
                          id="rhpWhitePrivilegeEnabled"
                        />
                        <p className="toggle-hint">Winners permanently expand max hand size</p>
                      </div>
                      <div className="setting-group">
                        <Toggle
                          checked={roomSettings.rhpDiscardEnabled}
                          onChange={(checked) => handleSettingChange('rhpDiscardEnabled', checked)}
                          label="Discard Mechanism"
                          enabledText="Discard Enabled"
                          disabledText="Discard Disabled"
                          id="rhpDiscardEnabled"
                        />
                        <p className="toggle-hint">Trade 2 cards for 1 (reduces max hand size)</p>
                      </div>
                      <div className="setting-group">
                        <Toggle
                          checked={roomSettings.rhpDoubleAwardEnabled}
                          onChange={(checked) =>
                            handleSettingChange('rhpDoubleAwardEnabled', checked)
                          }
                          label="Judge Double Award"
                          enabledText="Double Award Enabled"
                          disabledText="Double Award Disabled"
                          id="rhpDoubleAwardEnabled"
                        />
                        <p className="toggle-hint">Judge picks 2 winners once per game</p>
                      </div>
                      <div className="setting-group">
                        <Toggle
                          checked={roomSettings.rhpEnhancedWageringEnabled}
                          onChange={(checked) =>
                            handleSettingChange('rhpEnhancedWageringEnabled', checked)
                          }
                          label="Enhanced Wagering"
                          enabledText="Enhanced Wagering Enabled"
                          disabledText="Enhanced Wagering Disabled"
                          id="rhpEnhancedWageringEnabled"
                        />
                        <p className="toggle-hint">Wager multiple Chips and submit extra cards</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Game Summary */}
          <div className="game-summary">
            <h3 className="summary-title">🎮 Your Game Setup</h3>
            <div className="summary-grid">
              <div className="summary-stat">
                <span className="summary-value">{roomSettings.maxPlayers}</span>
                <span className="summary-label">Max Players</span>
              </div>
              <div className="summary-stat">
                <span className="summary-value">{roomSettings.roundsToWin}</span>
                <span className="summary-label">Rounds to Play</span>
              </div>
            </div>
            <div className="summary-features">
              🔒 Private Room • ⏰ Smart Timers • ✏️ Write-Ins Enabled
            </div>
          </div>
        </div>

        {/* Create Room Button */}
        <div className="create-room-section">
          <button
            className={`create-room-btn ${isCreating ? 'loading' : ''}`}
            onClick={handleCreateRoom}
            disabled={isCreating}
          >
            {isCreating ? 'Creating Room...' : 'Create New Room'}
          </button>

          <p className="create-room-description">
            You'll get a unique room code to share with friends
          </p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="back-link">
          <button onClick={handleBackToHome} className="back-btn" disabled={isCreating}>
            ← Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoomCodeEntry;
